<template>
  <div class="page">
    <CHeader v-bind="headerData" />
    <div class="contacts-fixed desktop-only">
      <div class="contacts-fixed__list">
        <ul v-if="config.socials" class="footer__socials">
          <template
            v-for="item in config.socials.filter(val =>
              ['vk', 'telegram', 'whatsapp', 'viber', 'wa'].includes(val.name.toLowerCase()),
            )"
          >
            <li v-if="item.link.length > 0" :key="item.name" class="footer__socials-item">
              <NuxtLink @click="socialsGoals(item)" :to="item.link" class="footer__socials-link" target="_blank">
                <NuxtIcon v-if="item.name" :name="`IconSocial${item.name}`" filled />
              </NuxtLink>
            </li>
          </template>
        </ul>
      </div>
    </div>
    <div class="page__content">
      <NuxtPage :page-key="pageKey" />
    </div>

    <CFooter v-bind="footerData" />
    <ul class="mob-menu">
      <li v-for="item in mobileMenu" :key="item.title" class="mob-menu__item">
        <NuxtLink
          :to="getMobLink(item)"
          class="mob-menu__link"
          :class="{
            'mob-menu__link--accent': isBookingItem(item),
            'mob-menu__link--pressed': mobMenuPressed === item.title,
          }"
          @click="onMobMenuClick($event, item)"
        >
          <UIPictureTag
            class="icon"
            :image-webp="`${url}${item.image.webp}`"
            :image-original="`${url}${item.image.source}`"
            :image-seo="item.image_seo"
          />
          {{ item.title }}
        </NuxtLink>
      </li>
      <!-- <li class="mob-menu__item">
        <div class="mob-menu__link" @click="$refs.buttonQuiz.click()">
          <div class="icon">
            <NuxtIcon name="IconPrice" filled />
          </div>
          Стоимость
        </div>
      </li> -->
    </ul>
    <!-- <div v-show="isShowQuiz" class="quiz wrapper">
      <NuxtIcon class="quiz__close" name="IconCancelSmall" @click="onHideQuiz"></NuxtIcon>
      <div class="quiz__txt">
        Рассчитайте предварительную цену, пройдя короткий тест за 20 секунд
      </div>
      <button
        ref="buttonQuiz"
        class="button button--size-l button--primary"
        type="button"
        id="buttonQuiz"
      >
        Рассчитать
      </button>
    </div> -->
    <CCookies />
  </div>
</template>

<script setup lang="ts">
import {
  useAsyncData,
  createError,
  useHead,
  provide,
  ref,
  useState,
  useSlugTransform,
  useRuntimeConfig,
  useNuxtApp,
  onMounted,
  watchEffect,
  useRouter,
  nextTick,
} from '#imports';
import type { ClientInitResponse } from '@/types/client/init';
import CHeader from '@/components/header/CHeader.vue';
import CFooter from '@/components/footer/CFooter.vue';
import type { MenuItem } from '~/types/menu';
import UIPictureTag from '~/components/UI/picture/UIPictureTag.vue';
import CCookies from '~/components/cookies/CCookies.vue';
import { useSchemaOrgSite } from '~/composables/useSchemaOrgSite';
import { yandexRichGoals } from '~/composables/yandexRichGoals';
import { openMangoWidget } from '~/composables/openMangoWidget';

const { goalsClickTg, goalsClickVk } = yandexRichGoals();

const router = useRouter();
const route = router.currentRoute.value;

const { data } = await useAsyncData<ClientInitResponse>('init', () =>
  $fetch('/api/init', {
    method: 'GET',
  }).catch(e => {
    throw createError(e);
  }),
);

const headerData = ref<{
  menuItems: MenuItem[];
  headerEmail: string;
  headerPhone: string;
}>();

const footerData = ref<{
  menuItems: MenuItem[];
  footerEmail: string;
  footerPhone: string;
}>();

const mobileMenu = ref<MenuItem[]>();

const url = useRuntimeConfig().public.siteUrl;

function getMobLink(item: MenuItem) {
  if (item.type === 'direct_link' && item.url) {
    return item.url;
  }

  return { name: 'home', params: { chapters: useSlugTransform(item.slug) } };
}

const mobMenuPressed = ref<string | null>(null);
let mobMenuPressedTimer: ReturnType<typeof setTimeout> | undefined;

const mobIconUrl = (iconPath: string) => {
  try {
    const iconUrl = new URL(`${url}${iconPath}`, window.location.origin);

    if (iconUrl.origin === window.location.origin) {
      return iconUrl.href;
    }

    return `/api/mob-icon?path=${encodeURIComponent(iconUrl.pathname)}`;
  } catch {
    return '';
  }
};

onMounted(() => {
  mobileMenu.value?.forEach(item => {
    const iconPath = item.image?.webp;

    if (iconPath) {
      new Image().src = mobIconUrl(iconPath);
    }
  });
});

/** «Записаться» в нижнем меню — единственный пункт с showCalltouch (он же акцентный). */
const isBookingItem = (item: MenuItem) => Boolean(item.url?.includes('showCalltouch'));

/**
 * Клик по нижнему меню на телефоне (110442): короткая подсветка нажатия, а «Записаться»
 * открывает виджет Манго. Если виджет не поднялся — ведём себя как раньше: переходим
 * по ссылке, которая открывает форму Calltouch.
 */
const onMobMenuClick = async (event: MouseEvent, item: MenuItem) => {
  const link = event.currentTarget as HTMLElement | null;

  if (link && item.image?.webp) {
    link.style.setProperty('--mob-menu-icon', `url('${mobIconUrl(item.image.webp)}')`);
  }

  mobMenuPressed.value = item.title;

  if (mobMenuPressedTimer) {
    clearTimeout(mobMenuPressedTimer);
  }

  mobMenuPressedTimer = setTimeout(() => {
    mobMenuPressed.value = null;
  }, 4000);

  if (!isBookingItem(item)) {
    return;
  }

  event.preventDefault();

  if (await openMangoWidget()) {
    return;
  }

  await router.push(getMobLink(item));
};

function pageKey(route: {
  fullPath: string;
  path: string;
  hash: string;
  query: Record<string, string | string[] | null | undefined>;
}) {
  if (!route.query?.showCalltouch) {
    return route.fullPath;
  }

  const query = { ...route.query };
  delete query.showCalltouch;

  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value == null) {
      continue;
    }
    for (const item of Array.isArray(value) ? value : [value]) {
      search.append(key, item);
    }
  }

  const qs = search.toString();
  return `${route.path}${qs ? `?${qs}` : ''}${route.hash || ''}`;
}

const config = useState('config');

if (data.value) {
  if (data.value.forms) {
    useState('forms', () => data.value?.forms);
  }

  if (data.value.affiliates) {
    useState('affiliates', () => data.value?.affiliates);
  }

  if (data.value.professions) {
    useState('professions', () => data.value?.professions);
  }

  if (data.value.servicesCatalog) {
    useState('servicesCatalog', () => data.value?.servicesCatalog);
  }

  if (data.value.config) {
    useState('config', () => data.value?.config);

    useHead({
      title: (data.value.config.siteName.value as string) || 'AO-СТОМ',
    });

    provide('pageCount', data.value.config.pageCount);
    provide('serviceCatalogAdult', data.value.config.serviceCatalogAdult);
    provide('serviceCatalogKids', data.value.config.serviceCatalogKids);
  }

  if (data.value.menu) {
    headerData.value = {
      menuItems: data.value.menu.header_menu,
      headerEmail: data.value.config?.headerEmail.value as string,
      headerPhone: data.value.config?.headerPhone.value as string,
    };

    footerData.value = {
      menuItems: data.value.menu.footer_menu,
      footerEmail: data.value.config?.footerEmail.value as string,
      footerPhone: data.value.config?.footerPhone.value as string,
    };

    mobileMenu.value = data.value.menu.footer_menu_mobile;
  }

  useSchemaOrgSite(data.value);
}

const nuxtApp = useNuxtApp();

// nuxtApp.hook('page:finish', () => {
//   window.scrollTo(0, 0);
// });

const loaded = ref(false);

const updateHead = () => {
  if (process.client) {
    loaded.value = false;

    const oldScript = document.getElementById('review-script');
    if (oldScript) oldScript.remove();

    const script = document.createElement('script');
    script.id = 'review-script';

    script.src = 'https://app.reviewlab.ru/widget/index-es2015.js';

    script.onerror = () => {
      const fallbackScript = document.createElement('script');
      fallbackScript.id = 'review-script';

      fallbackScript.src = 'https://app.reviewlab.ru/widget/index-es2015.js';
      fallbackScript.onload = () => {
        loaded.value = true;
      };
      fallbackScript.onerror = () => {
        console.log('Произошла ошибка при загрузке виджета');
      };
      document.head.appendChild(fallbackScript);
    };

    script.onload = () => {
      loaded.value = true;
    };
    document.head.appendChild(script);
  }
};

// const isShowQuiz = ref(false);

// onMounted(() => {
//   if (process.browser) {
//     isShowQuiz.value = !window.localStorage.getItem('hideQuiz');
//   }
// });

// const onHideQuiz = () => {
//   isShowQuiz.value = false;
//   window.localStorage.setItem('hideQuiz', 'true');
//   window.setTimeout(() => {
//     isShowQuiz.value = true;
//     window.localStorage.removeItem('hideQuiz');
//   }, 18000);
// };

// watchEffect(() => {
//   if (process.browser) {
//     window.addEventListener('storage', () => {
//       isShowQuiz.value = window.localStorage.getItem('hideQuiz') === 'true';
//     });
//   }
// });

const socialsGoals = (social) => {
  if (social.name === 'Telegram') goalsClickTg();
  if (social.name === 'Vk') goalsClickVk();
}

onMounted(() => {
  updateHead();
});
</script>
